import ReactECharts from 'echarts-for-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadingState } from '../common';
import { useEventContext } from '../../contexts/EventContext';
import { useBasicStatisticsQuery } from '../../hooks/useBasicStatisticsQuery';
import { useEventCollectFlagsQuery } from '../../hooks/useEventCollectFlagsQuery';
import { useGraphCategoriesQuery } from '../../hooks/useGraphCategoriesQuery';
import { useSplitDistributionQuery } from '../../hooks/useSplitDistributionQuery';
import { EVENT_LIST_ROUTE_PATH } from '../../routes/appRoutes';
import type { BasicStatisticChart } from '../../services/graphApi';
import { createAgePieOption } from './basicStatisticsChartOptions';
import StatisticsFilterBar, {
  emptyStatisticsFilters,
  toGraphFilters,
  uniqueFilterValues,
} from './StatisticsFilterBar';

function ChartCard({
  chart,
  empty,
}: Readonly<{
  chart: BasicStatisticChart;
  empty: boolean;
}>) {
  return (
    <section className="basic-stat-card" aria-label={`${chart.categoryName} 참가자 연령 통계 그래프`}>
      <div className="basic-stat-card-heading">
        <h2>{chart.categoryName}</h2>
        <span>{chart.totalParticipants.toLocaleString()}명</span>
      </div>
      {empty ? (
        <p className="basic-stat-empty">표시할 데이터가 없습니다.</p>
      ) : (
        <ReactECharts
          option={createAgePieOption(chart.categoryName, chart.ages)}
          style={{ height: 500 }}
        />
      )}
    </section>
  );
}

function BasicStatisticsDashboard() {
  const { selectedEventId } = useEventContext();
  const collectFlagsQuery = useEventCollectFlagsQuery(selectedEventId);
  const collectGender = collectFlagsQuery.data?.collectGender ?? true;
  const [filters, setFilters] = useState(emptyStatisticsFilters);

  // 백엔드 응답에 남아있는 값과 무관하게, 성별을 수집하지 않는 대회는 필터를 무조건 전체로 고정한다.
  const graphFilters = useMemo(() => {
    const nextFilters = toGraphFilters(filters);
    return {
      ...nextFilters,
      ageGroup: '',
      gender: collectGender ? nextFilters.gender : '',
    };
  }, [filters, collectGender]);
  const { data, error, isError, isFetching, isLoading, refetch, dataUpdatedAt } =
    useBasicStatisticsQuery(selectedEventId, graphFilters);
  const categoryQuery = useGraphCategoriesQuery(selectedEventId);
  const filterOptionQuery = useSplitDistributionQuery(selectedEventId);
  const categories = useMemo(() => categoryQuery.data ?? [], [categoryQuery.data]);
  const filterOptionParticipants = useMemo(
    () => filterOptionQuery.data?.participants ?? [],
    [filterOptionQuery.data?.participants]
  );
  const ages = useMemo(() => data?.ages ?? [], [data?.ages]);
  const charts = useMemo<BasicStatisticChart[]>(
    () => {
      if (data?.charts && data.charts.length > 0) {
        return data.charts;
      }

      return ages.length > 0
        ? [{
            categoryName: '전체',
            ages,
            totalParticipants: data?.totalParticipants ?? 0,
          }]
        : [];
    },
    [ages, data?.charts, data?.totalParticipants]
  );
  const genderOptions = useMemo(
    () => uniqueFilterValues(filterOptionParticipants.map((participant) => participant.gender)),
    [filterOptionParticipants]
  );
  const ageGroupOptions = useMemo(
    () => uniqueFilterValues(filterOptionParticipants.map((participant) => participant.ageGroup)),
    [filterOptionParticipants]
  );
  const hasAgeData = ages.some((age) => age.participantCount > 0);
  const errorMessage = error instanceof Error ? error.message : '통계 데이터를 불러오지 못했습니다.';
  const updatedAtText = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('ko-KR', { hour12: false })
    : '';
  let content = (
    <div className="basic-stat-chart-list">
      {charts.map((chart, index) => (
        <ChartCard
          chart={chart}
          empty={chart.ages.some((age) => age.participantCount > 0) === false}
          key={chart.categoryId ?? `${chart.categoryName}-${index}`}
        />
      ))}
    </div>
  );

  if (selectedEventId == null) {
    content = (
      <div className="basic-stat-message">
        선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
      </div>
    );
  } else if (isLoading) {
    content = <LoadingState message="통계 데이터를 불러오는 중입니다." />;
  } else if (hasAgeData === false) {
    content = <div className="basic-stat-message">표시할 통계 데이터가 없습니다.</div>;
  }

  return (
    <div className="basic-stat-page">
      <div className="basic-stat-header">
        <div className="page-title-block">
          <p className="page-section">Statistics</p>
          <h1>참가자 연령 통계</h1>
          <p className="page-description">참가자의 연령 분포를 요약해 확인합니다.</p>
        </div>
        <div className="basic-stat-actions">
          {updatedAtText && <span>최근 갱신 {updatedAtText}</span>}
          <button
            type="button"
            onClick={() => {
              void Promise.all([
                categoryQuery.refetch(),
                filterOptionQuery.refetch(),
                refetch(),
              ]);
            }}
            disabled={isFetching || categoryQuery.isFetching || filterOptionQuery.isFetching}
          >
            {isFetching ? '갱신 중' : '새로고침'}
          </button>
        </div>
      </div>

      <StatisticsFilterBar
        filters={filters}
        categories={categories}
        genderOptions={genderOptions}
        ageGroupOptions={ageGroupOptions}
        className="statistics-filter-row--basic"
        disabled={isFetching || categoryQuery.isFetching}
        showAgeGroupFilter={false}
        collectGender={collectGender}
        onChange={setFilters}
        onReset={() => setFilters(emptyStatisticsFilters)}
      />

      {isError && (
        <div className="basic-stat-message basic-stat-error" role="alert">
          {errorMessage}
        </div>
      )}

      {content}
    </div>
  );
}

export default BasicStatisticsDashboard;
